import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Play, Stethoscope } from "lucide-react";
import { ASSESSMENT_TYPES, TYPE_META } from "./assessmentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StartAssessmentDialog({ open, onClose, clientId, clientName, defaultType = "admission", defaultVisitDate, lockType = false, onStarted }) {
  const [clients, setClients] = useState(null);
  const [selectedClient, setSelectedClient] = useState(clientId || "");
  const [type, setType] = useState(defaultType);
  const [catalog, setCatalog] = useState(null);
  const [multi, setMulti] = useState(new Set()); // every form in the packet, pre-selected
  const [revisable, setRevisable] = useState([]); // completed recurring forms (e.g. Plan of Care) for this client
  const [revise, setRevise] = useState(new Set());
  const [visitDate, setVisitDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const isPaperwork = !!TYPE_META[type]?.paperwork;

  useEffect(() => {
    if (!open) return;
    setType(defaultType);
   
    setMulti(new Set());
    setSelectedClient(clientId || "");
    setVisitDate(defaultVisitDate || new Date().toISOString().slice(0, 10));
    axios.get(`${API}/assessments/templates`).then((r) => setCatalog(r.data.types)).catch(() => toast.error("Could not load assessment forms"));
    if (!clientId) axios.get(`${API}/clients`).then((r) => setClients(r.data || [])).catch(() => setClients([]));
  }, [open, clientId, defaultType, defaultVisitDate]);

  useEffect(() => {
    if (!open || !selectedClient || type !== "supervisory") { setRevisable([]); setRevise(new Set()); return; }
    axios.get(`${API}/assessments/revisable`, { params: { client_id: selectedClient } })
      .then((r) => {
        const forms = r.data.forms || [];
        setRevisable(forms);
        setRevise(new Set(forms.filter((f) => !f.cycle_complete && !f.update_in_progress).map((f) => f.id)));
      })
      .catch(() => setRevisable([]));
  }, [open, selectedClient, type]);
  const toggleRevise = (id) => setRevise((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const templates = (catalog || []).find((t) => t.value === type)?.templates || [];
  const ready = templates.filter((t) => t.ready);
  useEffect(() => { setMulti(new Set(ready.map((t) => t.id))); }, [type, catalog]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleMulti = (id) => setMulti((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const canStart = selectedClient && (multi.size > 0 || revise.size > 0);

  const start = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/assessments/bulk`, { client_id: selectedClient, assessment_type: type, template_ids: [...multi], revise_ids: [...revise], visit_date: visitDate });
      toast.success(r.data.count === 1 ? `${TYPE_META[type]?.label} started` : `${TYPE_META[type]?.label} started — ${r.data.count} forms in this visit`);
      onStarted?.(r.data.started[0], r.data.started);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start assessment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="start-assessment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Stethoscope className="w-5 h-5 text-rose-600" /> {isPaperwork ? "Admission Forms" : "New RN Assessment"}</DialogTitle>
          <DialogDescription>{clientName ? `For ${clientName}` : "Pick the client, the visit type and the agency form to complete."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!clientId && (
            <div>
              <Label className="text-xs">Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger data-testid="assessment-client-select"><SelectValue placeholder={clients === null ? "Loading…" : "Select a client"} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {!lockType && (
          <div>
            <Label className="text-xs">Visit type</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1" data-testid="assessment-type-picker">
              {ASSESSMENT_TYPES.map((t) => {
                const Icon = t.icon;
                const active = type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => { setType(t.value); setMulti(new Set()); }}
                    className={`rounded-lg border px-2 py-3 text-xs font-medium flex flex-col items-center gap-1.5 transition-colors ${active ? "border-rose-500 bg-rose-50 text-rose-800" : "border-slate-200 hover:border-slate-300 text-slate-600"}`}
                    data-testid={`assessment-type-${t.value}`}
                  >
                    <Icon className="w-4 h-4" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          )}

          <div>
            <Label className="text-xs">{isPaperwork ? "Admission forms to fill" : "Forms in this visit (all pre-selected)"}</Label>
            {catalog && templates.length === 0 ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mt-1" data-testid="assessment-no-template">
                No form uploaded for this visit type yet. Upload your agency's PDF under{" "}
                <Link to="/settings?tab=form-templates" className="underline font-medium">Settings → Form Templates</Link> with the category “{isPaperwork ? "Admission Forms (client packet)" : `RN Assessment — ${TYPE_META[type]?.label}`}”.
              </p>
            ) : (
              <div className="mt-1 max-h-56 overflow-y-auto divide-y divide-slate-100 border rounded-md" data-testid="admission-forms-multiselect">
                <div className="flex items-center justify-between px-3 py-1.5 text-[11px] text-slate-500 bg-slate-50">
                  <span>{multi.size} of {ready.length} selected{multi.size > 1 ? " — all open as one visit" : ""}</span>
                  <button type="button" className="underline" onClick={() => setMulti(new Set(ready.map((t) => t.id)))} data-testid="admission-forms-select-all">Select all</button>
                </div>
                {templates.map((t) => (
                  <label key={t.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${t.ready ? "hover:bg-slate-50 cursor-pointer" : "opacity-50"}`}>
                    <Checkbox checked={multi.has(t.id)} disabled={!t.ready} onCheckedChange={() => toggleMulti(t.id)} data-testid={`admission-form-check-${t.id}`} />
                    <span className="flex-1 truncate">{t.title}</span>
                    {!t.ready && <span className="text-[10px] text-amber-700">fields not configured</span>}
                  </label>
                ))}
              </div>
            )}
          </div>

          {type === "supervisory" && revisable.length > 0 && (
            <div data-testid="forms-to-update">
              <Label className="text-xs">Forms to update at this visit (re-sign & date)</Label>
              <div className="mt-1 divide-y divide-slate-100 border rounded-md border-sky-200 bg-sky-50/40">
                {revisable.map((f) => {
                  const blocked = f.cycle_complete || f.update_in_progress;
                  return (
                    <label key={f.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${blocked ? "opacity-60" : "hover:bg-sky-50 cursor-pointer"}`}>
                      <Checkbox checked={revise.has(f.id)} disabled={blocked} onCheckedChange={() => toggleRevise(f.id)} data-testid={`revise-check-${f.id}`} />
                      <span className="flex-1 truncate">{f.template_title}</span>
                      <span className="text-[10px] text-slate-500">
                        {f.cycle_complete ? `Cycle complete (${f.recurring_count}/${f.recurring_count}) — start a new form at re-admission`
                          : f.update_in_progress ? "Update already in progress"
                          : `Update ${f.next_revision} of ${f.recurring_count} · last ${f.visit_date}`}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Visit date</Label>
            <Input type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} data-testid="assessment-visit-date" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={start} disabled={busy || !canStart} className="gap-1 bg-rose-600 hover:bg-rose-700" data-testid="start-assessment-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} {multi.size + revise.size > 1 ? `Start ${multi.size + revise.size} forms` : revise.size === 1 && multi.size === 0 ? "Start update" : "Start assessment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
